import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  nextTick,
  inject,
  getCurrentInstance,
} from 'vue';

import useGeocode from '~common/composables/useGeocode';
import useCurrentLocation from '~composables/useCurrentLocation';

function getRotationAngle(currentHeading, prevHeading) {
  const diff = currentHeading - prevHeading;
  if (diff < -180) {
    return diff + 360;
  }
  if (diff > 180) {
    return diff - 360;
  }
  return diff;
}

export default function useMap(props, init) {
  const isCabinet = inject('isCabinet');
  const isWeb = inject('isWeb');
  const domain = inject('domain');

  const { emit } = getCurrentInstance();
  const { initGeocode, geocode, pointToLatLng } = useGeocode();
  const { startWatchLocation, stopWatchLocation } = useCurrentLocation();

  const markers = {};

  let userIcons;
  let isAutoZoom;
  let userCoords;
  let dragstartListener;
  let zoomChangedListener;
  let idleListener;
  let currentAngle;
  let userMarkerStyleElement;
  let compassWatchId;
  let isAlreadyDestroyed;
  const driverStyles = [];

  const map = ref(null);
  const googleMapRef = ref(null);
  const disableZoom = ref(!!props.directionToZoom);
  const userHeading = ref(null);

  const hasHeading = computed(() => typeof userHeading.value === 'number');
  const userIconName = computed(() => {
    if (hasHeading.value) {
      return props.userMarkerAnimated ? 'headingAnimated' : 'heading';
    }
    if (props.userMarkerAnimated) {
      return 'animated';
    }
    return 'common';
  });

  function removeUserMarkerStyle() {
    userMarkerStyleElement?.remove();
    userMarkerStyleElement = null;
  }

  function removeDisableZoomListeners() {
    if (dragstartListener) {
      google.maps.event.removeListener(dragstartListener, 'dragstart');
    }
    if (zoomChangedListener) {
      google.maps.event.removeListener(zoomChangedListener, 'zoom_changed');
    }
    if (idleListener) {
      google.maps.event.removeListener(idleListener, 'idle');
    }
  }

  function addDisableZoomListeners() {
    dragstartListener ||= google.maps.event.addListener(map.value, 'dragstart', () => {
      disableZoom.value = true;
      removeDisableZoomListeners();
    });
    zoomChangedListener ||= google.maps.event.addListener(map.value, 'zoom_changed', () => {
      if (!isAutoZoom) {
        disableZoom.value = true;
        removeDisableZoomListeners();
      }
    });
    idleListener ||= google.maps.event.addListener(map.value, 'idle', () => {
      isAutoZoom = false;
    });
  }

  function setMapCenter(position, setZoom = true) {
    // centering doesn't work properly without zoom in
    if (map.value.getZoom() < 2) {
      map.value.setZoom(2);
    }
    map.value.setCenter(position);
    if (setZoom) {
      map.value.setZoom(typeof setZoom === 'number' ? setZoom : 13);
    }
  }

  function zoomMarker() {
    isAutoZoom = true;

    const zoomedMarker = markers[props.directionToZoom];
    if (zoomedMarker) {
      setMapCenter(zoomedMarker.position, 11.6); // 11.6 zoom level ≈ 10 km on 360px map width;
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    let count = 0;
    let existingMarker;

    const keys = isCabinet.carrier ? Object.keys(markers) : ['from', 'to'];
    keys.forEach((key) => {
      if (props.zoomPassengerMarker && key === 'to') return;

      const marker = markers[key];
      if (marker) {
        bounds.extend(marker.position);
        existingMarker = marker;
        count += 1;
      }
    });

    if (count === 0) return;
    if (count === 1) setMapCenter(existingMarker.position);
    if (count > 1) map.value.fitBounds(bounds);
  }

  function setUserHeading() {
    if (isWeb) return;
    currentAngle = 0;
    const onSuccess = ({ trueHeading, magneticHeading }) => {
      const prevHeading = userHeading.value || 0;
      userHeading.value = typeof trueHeading === 'number' && trueHeading >= 0
        ? trueHeading
        : magneticHeading;

      if (!userMarkerStyleElement) {
        userMarkerStyleElement = document.createElement('style');
        document.head.appendChild(userMarkerStyleElement);
      }
      currentAngle += getRotationAngle(userHeading.value, prevHeading);
      userMarkerStyleElement.innerHTML = `img[src="${userIcons[userIconName.value].url}"] {
          transition-duration: .5s;
          transform: rotate(${currentAngle}deg);
        }`;
    };
    const onError = () => {
      userHeading.value = null;
      window.navigator.compass.clearWatch(compassWatchId);
    };
    compassWatchId = window.navigator.compass.watchHeading(onSuccess, onError, { frequency: 500 });
  }

  async function createMap() {
    googleMapRef.value.childNodes.forEach((child) => child.remove());
    const { map: mapInstance, div } = window.googleMapInstance;

    map.value = mapInstance;

    map.value.setOptions({
      center: props.gmapCenter,
      disableDefaultUI: true,
      zoom: props.gmapZoom,
      scrollwheel: true,
      mapTypeControl: false,
      streetViewControl: false,
      disableDoubleClickZoom: true,
      gestureHandling: props.gestureHandling,
    });
    googleMapRef.value?.appendChild(div);
  }

  function clearMarker(key) {
    const marker = markers[key];
    if (marker && google.maps) {
      google.maps.event.clearListeners(marker, 'click');
      marker.setMap(null);
      marker.remove?.();
      markers[key] = null;
    }
  }

  function clearMarkers() {
    if (markers) {
      Object.keys(markers).forEach(clearMarker);
    }
  }

  function saveUserIconsData() {
    userIcons = {
      common: {
        url: `svg/${domain.name}/location.svg`,
        anchor: new google.maps.Point(18, 18),
      },
      animated: {
        url: 'common/location_icons/location_animated.png',
        scaledSize: new google.maps.Size(60, 60),
        anchor: new google.maps.Point(30, 30),
      },
      heading: {
        url: 'common/location_icons/heading.svg',
        anchor: new google.maps.Point(45, 45),
      },
      headingAnimated: {
        url: 'common/location_icons/heading_animated.png',
        scaledSize: new google.maps.Size(86, 86),
        anchor: new google.maps.Point(45, 45),
      },
    };
  }

  function setUserCoords({ coords }) {
    userCoords = new google.maps.LatLng(coords.latitude, coords.longitude);

    if (markers.user) {
      markers.user.setPosition(userCoords);
    } else {
      markers.user = new google.maps.Marker({
        clickable: false,
        position: userCoords,
        icon: userIcons[userIconName.value],
        optimized: false,
      });
      markers.user.setMap(map.value);
    }
  }

  async function baseInit() {
    await nextTick();
    if (isAlreadyDestroyed) return;

    await createMap();

    if (initGeocode) initGeocode();
    if (init) init();

    saveUserIconsData();
    setUserHeading();
    startWatchLocation(setUserCoords, { checkPermissions: true });

    if (props.userMarkerChangesZoom) {
      addDisableZoomListeners();
    }

    emit('map-initialized');
  }

  function setMarkerByKey(key, latLngArr, options = {}) {
    const position = new google.maps.LatLng(...latLngArr);
    if (markers[key]) {
      markers[key].setPosition(position);
      return;
    }

    markers[key] = new google.maps.Marker({
      position: position,
      optimized: false,
      ...options,
    });
    markers[key].addListener('click', () => {
      emit('select-driver', key);
    });
    markers[key].setMap(map.value);
  }

  function newLocation({
    id, full_name, iconUrl, anchor = [20, 20], scaledSize = [40, 40],
  }, { latitude, longitude, heading }) {
    setMarkerByKey(
      id,
      [latitude, longitude],
      {
        label: full_name,
        icon: {
          url: iconUrl,
          anchor: new google.maps.Point(...anchor),
          origin: new google.maps.Point(0, 0),
          scaledSize: new google.maps.Size(...scaledSize),
          labelOrigin: new google.maps.Point(75, 5),
        },
      },
    );

    const selector = `img[src="${iconUrl}"]`;
    const cssRule = `transform: rotate(${heading}deg);`;
    driverStyles.push(`${selector} {${cssRule}}`);
    if (!disableZoom.value) {
      zoomMarker();
    }
  }

  watch(userIconName, (value) => {
    markers.user?.setIcon(userIcons[value]);
    removeUserMarkerStyle();
  });

  onMounted(() => {
    if (window.googleMapInstance) {
      baseInit();
    }
    window.addEventListener('google_init', baseInit);
  });

  onBeforeUnmount(() => {
    isAlreadyDestroyed = true;
    stopWatchLocation();
    window.removeEventListener('google_init', baseInit);
    removeDisableZoomListeners();
    removeUserMarkerStyle();
    window.navigator.compass?.clearWatch(compassWatchId);
  });

  return {
    map,
    markers,
    disableZoom,
    clearMarker,
    clearMarkers,
    zoomMarker,
    googleMapRef,
    geocode,
    setMapCenter,
    pointToLatLng,
    addDisableZoomListeners,
    newLocation,
    driverStyles,
  };
}
